import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Clock, RefreshCw, Search } from "lucide-react";
import { format } from "date-fns";

interface CodOrder {
  id: string;
  seller_id: string | null;
  total_amount: number;
  delivery_fee: number | null;
  status: string | null;
  payment_status: string | null;
  delivery_type: string | null;
  recipient_name: string | null;
  recipient_phone: string | null;
  delivery_city: string | null;
  delivery_state: string | null;
  created_at: string;
  updated_at: string;
}

type Filter = "pending" | "completed" | "all";

export function CodPayments({ onConfirmed }: { onConfirmed?: () => void }) {
  const { toast } = useToast();
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [sellers, setSellers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("pending");
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState<CodOrder | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("orders")
      .select("id, seller_id, total_amount, delivery_fee, status, payment_status, delivery_type, recipient_name, recipient_phone, delivery_city, delivery_state, created_at, updated_at")
      .eq("payment_method", "pay_on_delivery")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: "Could not load pay on delivery orders", variant: "destructive" });
      setLoading(false);
      return;
    }
    const list = (data || []) as CodOrder[];
    setOrders(list);
    const ids = [...new Set(list.map((o) => o.seller_id).filter(Boolean))] as string[];
    if (ids.length) {
      const { data: profs } = await supabase.from("profiles").select("id, business_name, full_name").in("id", ids);
      setSellers(Object.fromEntries((profs || []).map((p: any) => [p.id, p.business_name || p.full_name || "Seller"])));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (o.status === "cancelled" && filter !== "all") return false;
      if (filter === "pending" && o.payment_status === "completed") return false;
      if (filter === "completed" && o.payment_status !== "completed") return false;
      if (!q) return true;
      return [o.id, o.recipient_name, o.recipient_phone, sellers[o.seller_id || ""]]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [orders, filter, search, sellers]);

  const pending = orders.filter((o) => o.payment_status !== "completed" && o.status !== "cancelled");
  const confirmed = orders.filter((o) => o.payment_status === "completed");
  const sum = (l: CodOrder[]) => l.reduce((s, o) => s + Number(o.total_amount || 0), 0);

  const confirm = async () => {
    if (!target) return;
    setSaving(true);
    const { data, error } = await supabase.rpc("admin_confirm_cod_payment" as any, { p_order_id: target.id, p_note: note || null });
    setSaving(false);
    if (error) {
      toast({ title: "Could not confirm", description: error.message, variant: "destructive" });
      return;
    }
    const res = data as any;
    toast({
      title: "Payment confirmed",
      description: res?.released
        ? "Seller's earnings have been released to their wallet."
        : res?.skipped_escrow
          ? "Payment recorded. Escrow was already updated for this order."
          : "Seller's earnings are now held in escrow until delivery is confirmed.",
    });
    setTarget(null);
    setNote("");
    load();
    onConfirmed?.();
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Awaiting confirmation</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦{sum(pending).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{pending.length} order(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Money received</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦{sum(confirmed).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{confirmed.length} order(s)</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pay on Delivery payments</CardTitle>
          <CardDescription>Confirm once the cash from the rider or seller has reached you. This updates the seller's escrow.</CardDescription>
          <div className="flex flex-col gap-2 pt-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search order, buyer, seller" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="flex gap-2">
              {(["pending", "completed", "all"] as Filter[]).map((f) => (
                <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}>
                  {f === "pending" ? "Awaiting" : f === "completed" ? "Received" : "All"}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="py-8 text-center text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No orders here.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      <div className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</div>
                      <div className="text-xs text-muted-foreground">{format(new Date(o.created_at), "dd MMM yyyy")}</div>
                    </TableCell>
                    <TableCell>
                      <div>{o.recipient_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.recipient_phone}</div>
                    </TableCell>
                    <TableCell>{sellers[o.seller_id || ""] || "—"}</TableCell>
                    <TableCell className="font-medium">₦{Number(o.total_amount).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="text-xs">{o.delivery_type === "fez" ? "Fez rider" : "Seller"}</div>
                      <Badge variant="outline" className="capitalize">{(o.status || "").replace(/_/g, " ")}</Badge>
                    </TableCell>
                    <TableCell>
                      {o.payment_status === "completed" ? (
                        <Badge><CheckCircle className="mr-1 h-3 w-3" />Received</Badge>
                      ) : (
                        <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Awaiting</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {o.payment_status !== "completed" && o.status !== "cancelled" && (
                        <Button size="sm" onClick={() => setTarget(o)}>Confirm receipt</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm money received?</AlertDialogTitle>
            <AlertDialogDescription>
              Order #{target?.id.slice(0, 8).toUpperCase()} — ₦{Number(target?.total_amount || 0).toLocaleString()}.
              {target && ["delivered", "completed"].includes(target.status || "")
                ? " The order is delivered, so the seller's earnings (minus commission) will go straight to their wallet."
                : " The seller's earnings will be held in escrow and released when delivery is confirmed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea placeholder="Note (optional), e.g. Fez remittance ref" value={note} onChange={(e) => setNote(e.target.value)} />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={saving} onClick={(e) => { e.preventDefault(); confirm(); }}>
              {saving ? "Confirming…" : "Confirm receipt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
